<template>
  <AppWithLoading :fetch="getArticle" v-slot:default="{loader}">
    <div class="article-create-form">
      <div class="article-create-form__panel d-flex align-items-center">
        <div class="d-flex align-items-center">
          <button class="article-create-form__link" @click="back">
            <img src="../../assets/img/arrow-left.svg" alt="">
            {{ $t('goBack') }}
          </button>
        </div>
        <div class="flex-fill" />
        <div class="d-flex align-items-center">
          <VBtn
            v-if="!isEditMode"
            class="article-create-form__btn"
            color="#F3F3F3"
            @click="back"
          >
            <span class="v-btn__text">{{ $t('cancel') }}</span>
          </VBtn>
          <VBtn
            v-if="isEditMode && company.companyTemplateSettings"
            class="article-create-form__btn ml-3"
            color="#F3F3F3"
            @click="loader(() => preview())"
          >
            <span class="v-btn__text">{{ $t('preview') }}</span>
          </VBtn>
          <VBtn
            v-if="isEditMode && editArticle.status && editArticle.status === articleStatus.PUBLISHED"
            class="article-create-form__btn publish ml-3"
            color="#f44336"
            @click="loader(() => removePublicationAndChangeStatus(editArticle.id))"
          >
            <span class="v-btn__text">{{ $t('removePublication') }}</span>
          </VBtn>
          <VBtn
            v-else
            class="article-create-form__btn publish ml-3"
            color="#1FC57F"
            @click="loader(() => publish())"
          >
            <span class="v-btn__text">{{ $t('publish') }}</span>
          </VBtn>
        </div>
      </div>

      <div class="article-create-form__container">
        <h2 v-if="errorFoundArticle" class="article-create-form__not-found">
          {{ $t('notFound') }}
        </h2>
        <template v-else>
          <ArticleFormField
            class="article-create-form-field__cover"
            :label="$t('cover')"
            :show="Boolean(articleCoverName)"
          >
            <VFileInput
              id="articleCoverButton"
              :label="$t('cover')"
              v-model="image"
              accept=".jpg,.png,.jpeg"
              @change="afterImage"
              v-show="false"
            />

            <VImg
              v-if="previewUrl"
              class="article-create-form__preview"
              width="300px"
              :src="previewUrl"
            />

            <label class="article-create-form__control" v-if="articleCoverName">
              {{ articleCoverName }}
            </label>

            <label
              for="articleCoverButton"
              class="article-create-form__control article-create-form__image-label"
              :class="{'article-create-form__image-label--isPreview': previewUrl }"
              @change="afterImage"
            >
              <div class="article-create-form__image-label_icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <g clip-path="url(#clip0_4021_653)">
                    <path d="M16.0745 7.87527L16.0745 6.12471L7.87487 6.12512L7.87528 -2.07455H6.12472L6.12513 6.12512L-2.07454 6.12471L-2.07454 7.87527L6.12513 7.87486L6.12472 16.0745H7.87528L7.87487 7.87486L16.0745 7.87527Z" fill="#898989" />
                  </g>
                  <defs>
                    <clipPath id="clip0_4021_653">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <span>
                {{
                  (editArticle && editArticle.previewUrl) ? $t('articleCoverChange') : $t('articleCover')
                }}</span>
            </label>

            <ArticleFormFieldErrors :errors="errors['image']" />
          </ArticleFormField>

          <ArticleFormField
            class="article-create-form-field__title"
            :label="$t('titleShort')"
            :show="Boolean(editArticle.title)"
          >
            <VTextField
              :placeholder="$t('title')"
              class="article-create-form__control"
              v-model="editArticle.title"
              :error-messages="errors['title']"
              hide-details="auto"
              @input="errors.title = null"
            />
            <div class="article-create-form__title-short" :data-title="$t('titleShort')" />
          </ArticleFormField>

          <ArticleFormField
            class="article-create-form-field__author"
            :label="$t('authorShort')"
            :show="Boolean(editArticle.author)"
          >
            <VTextField
              :placeholder="$t('author')"
              class="article-create-form__control"
              v-model="editArticle.author"
              :error-messages="errors['author']"
              hide-details="auto"
              @input="errors.author = null"
            />
            <div class="article-create-form__title-short" :data-title="$t('authorShort')" />
          </ArticleFormField>

          <ArticleFormField
            class="article-create-form-field__text"
            :label="$t('placeholderEditorShort')"
            :show="Boolean(editArticle.content)"
          >
            <quill-editor
              v-model="editArticle.content"
              class="editor"
              ref="blogEditorRef"
              :options="state.editorOption"
            />
            <ArticleFormFieldErrors :errors="errors['content']" />
            <div class="article-create-form__editor">
              <button
                ref="imageButtonRef"
                id="imageButton"
                class="article-create-form__editor-btn"
                v-show="isImageButtonActive"
                :style="{top: `${imageButtonTop}px`}"
                @click="addImage"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_1454_119)">
                    <path
                      d="M11.375 0H2.625C1.92904 0.000771999 1.26181 0.277582 0.769697 0.769697C0.277582 1.26181 0.000771999 1.92904 0 2.625L0 11.375C0.000771999 12.071 0.277582 12.7382 0.769697 13.2303C1.26181 13.7224 1.92904 13.9992 2.625 14H11.375C12.071 13.9992 12.7382 13.7224 13.2303 13.2303C13.7224 12.7382 13.9992 12.071 14 11.375V2.625C13.9992 1.92904 13.7224 1.26181 13.2303 0.769697C12.7382 0.277582 12.071 0.000771999 11.375 0V0ZM2.625 1.75H11.375C11.6071 1.75 11.8296 1.84219 11.9937 2.00628C12.1578 2.17038 12.25 2.39294 12.25 2.625V11.375C12.2502 11.4899 12.2276 11.6037 12.1835 11.7098C12.1394 11.816 12.0748 11.9123 11.9933 11.9933L6.89967 6.89967C6.4621 6.46223 5.86872 6.2165 5.25 6.2165C4.63128 6.2165 4.0379 6.46223 3.60033 6.89967L1.75 8.75V2.625C1.75 2.39294 1.84219 2.17038 2.00628 2.00628C2.17038 1.84219 2.39294 1.75 2.625 1.75Z"
                      fill="black"
                    />
                    <path
                      d="M9.04183 5.83329C9.84724 5.83329 10.5002 5.18037 10.5002 4.37496C10.5002 3.56954 9.84724 2.91663 9.04183 2.91663C8.23641 2.91663 7.5835 3.56954 7.5835 4.37496C7.5835 5.18037 8.23641 5.83329 9.04183 5.83329Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1454_119">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </ArticleFormField>
        </template>
      </div>
    </div>
  </AppWithLoading>
</template>

<script>
import {
  computed, ref, toRefs, watch, nextTick,
} from '@vue/composition-api';
import AppWithLoading from '@/components/AppWithLoading';
import ArticleFormField from '@/components/Company/ArticleFormField';
import ArticleFormFieldErrors from '@/components/Company/ArticleFormFieldErrors';
import useArticle from '@/compositions/useArticle';
import useValidation from '@/compositions/useValidation';
import { useI18n } from 'vue-i18n-composable';
import useCompany from '@/compositions/useCompany';
import { articleStatuses, portalTabs } from '@/enums';
import ImageCompress from 'quill-image-compress';
import Quill from 'quill';

Quill.register('modules/imageCompress', ImageCompress);

export default {
  name: 'ArticleForm',
  components: {
    AppWithLoading,
    ArticleFormField,
    ArticleFormFieldErrors,
  },
  props: {
    article: {
      type: Object,
      required: false,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, {
    root: {
      $route,
      $router,
    },
  }) {
    const { isEditMode } = toRefs(props);

    const editArticle = ref({
      title: '',
      author: '',
      content: '',
      previewUrl: '',
    });

    const errorFoundArticle = ref(false);

    const { t } = useI18n();

    const {
      withValidationHandling,
      errors,
      hasErrors,
    } = useValidation();
    const {
      createArticle,
      updateArticle,
      publishArticle,
      fetchArticle,
      removePublication,
    } = useArticle();
    const { company } = useCompany();

    const title = computed(() => editArticle?.value?.title);
    const author = computed(() => editArticle?.value?.author);
    const image = ref(null);
    const articleCoverName = ref('');
    const content = computed(() => editArticle?.value?.content);
    const previewUrl = computed(() => {
      if (image?.value) {
        return URL.createObjectURL(image.value);
      }
      if (editArticle?.value?.previewUrl) {
        return editArticle.value.previewUrl;
      }
      return null;
    });

    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],

      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],

      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
    ];

    const state = ref({
      editorOption: {
        theme: 'snow',
        scrollingContainer: 'html',
        placeholder: t('placeholderEditor'),
        modules: {
          toolbar: {
            container: [['image'], ...toolbarOptions],
          },
          imageCompress: {
            quality: 0.7,
            keepImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
          },
        },
      },
    });

    const blogEditorRef = ref(null);
    const isImageButtonActive = ref(false);
    const imageButtonTop = ref(0);

    function calculateImageButtonPosition(e) {
      nextTick(() => {
        if (!e.quill) {
          isImageButtonActive.value = true;
        }

        const quill = e.quill ? e.quill : e;
        if (!quill?.getSelection()) {
          return;
        }

        const { top } = quill.getBounds(quill.getSelection());
        imageButtonTop.value = top - 2;
        isImageButtonActive.value = true;
      });
    }

    // onMounted(() => {
    //   blogEditorRef.value.$el.addEventListener('click', clickGlobalHandler);
    //   blogEditorRef.value.$el.addEventListener('keyup', clickGlobalHandler);
    // });
    // onUnmounted(() => {
    //   blogEditorRef.value.$el.removeEventListener('click', clickGlobalHandler);
    // });

    async function readFile(url) {
      return new Promise((resolve) => {
        fetch(url)
          .then((res) => {
            res.blob()
              .then((blob) => {
                const file = new File(
                  [blob],
                  url.substring(url.lastIndexOf('/') + 1),
                  { type: blob.type },
                );
                resolve(file);
              });
          });
      });
    }

    async function publish() {
      let imageFile = image?.value;
      if (imageFile) {
        imageFile = image.value;
      } else if (editArticle?.value?.previewUrl) {
        imageFile = await readFile(editArticle.value.previewUrl);
      } else {
        imageFile = null;
      }

      const saveData = {
        title: title.value,
        author: author.value,
        image: imageFile,
        content: content.value,
      };

      await withValidationHandling(async () => {
        await publishArticle(editArticle.value.id, saveData);
        editArticle.value.status = articleStatuses.PUBLISHED;
      });

      // после создании редирект на редактирование
      if (!hasErrors.value && !isEditMode.value) {
        await $router.push({
          name: 'article-edit',
          params: { id: editArticle.value.id },
        });
      }
    }

    async function afterImage(file) {
      articleCoverName.value = file.name;
    }

    async function preview() {
      if (!company.value.companyTemplateSettings) {
        return;
      }

      const careerPortalUrl = process.env.VUE_APP_CAREER_PORTAL_URL;
      const a = document.createElement('a');
      a.target = '_blank';
      a.href = `${careerPortalUrl}preview/${company.value.companyTemplateSettings.template.name}/${company.value.id}/blog/${editArticle.value.id}`;
      await a.click();
    }

    async function back() {
      await $router.push({
        name: 'career-portal',
        params: {
          type: portalTabs.ARTICLES,
        },
      });
    }

    const getArticle = async () => {
      if (isEditMode.value) {
        try {
          editArticle.value = await fetchArticle($route.params.id);
        } catch (ev) {
          errorFoundArticle.value = true;
        }
      }
    };

    const articleStatus = ref(articleStatuses);

    async function removePublicationAndChangeStatus(id) {
      await removePublication(id);
      editArticle.value.status = articleStatuses.DRAFT;
    }

    async function save() {
      const saveData = {
        title: title.value,
        author: author.value,
        image: image.value,
        content: content.value,
      };

      if (isEditMode.value) {
        await updateArticle(editArticle.value.id, saveData);
      } else {
        editArticle.value = await createArticle(saveData);
        await $router.push({
          name: 'article-edit',
          params: { id: editArticle.value.id },
        });
      }
    }

    let wait = false;

    async function saveWithTimeout() {
      if (wait) {
        return;
      }

      wait = true;
      setTimeout(async () => {
        await save();
        wait = false;
      }, 3000);
    }

    function addImage() {
      document.querySelector('.ql-image')
        .click();
    }

    watch(title, () => saveWithTimeout());
    watch(author, () => saveWithTimeout());
    watch(content, () => saveWithTimeout());
    watch(image, () => saveWithTimeout());

    return {
      image,
      previewUrl,
      articleCoverName,
      errors,
      afterImage,
      state,
      publish,
      company,
      preview,
      editArticle,
      back,
      getArticle,
      errorFoundArticle,
      articleStatus,
      removePublicationAndChangeStatus,
      addImage,
      imageButtonTop,
      blogEditorRef,
      isImageButtonActive,
      calculateImageButtonPosition,
    };
  },
};
</script>

<style scoped lang="scss">
.article-create-form {
  padding: 0 20px;
  &::v-deep {
    .article-create-form-field__content {
      position: relative;
    }
  }

  &__image-label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    color: #898989;
    font-size: 13px;
    font-weight: 400;
    line-height: 120%;
    &_icon {
      border-radius: 50px;
      border: 1px solid #D9D9D9;
      margin-right: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 36px;
      width: 36px;
      svg {
      }
    }
  }
  &__image-label--isPreview {
    position: absolute;
    right: -10%;
    top: 40%;
    @media screen and (max-width: 768px) {
      position: static;
      margin-top: 30px;
    }
  }

  &__not-found {
    @media (max-width: 992px) {
      font-size: 16px;
    }
  }

  &__panel {
    position: relative;
    padding: 25px 0;

    @media (max-width: 992px) {
      padding: 20px 0;
    }
  }

  &__container {
    padding: 40px 80px;
    border-radius: 10px;
    background: #FFF;
    min-height: 88vh;
    @media screen and (max-width: 992px) {
      padding: 40px;
      min-height: 90vh;
    }
    @media screen and (max-width: 599px) {
      padding: 40px;
      min-height: 83vh;
    }
    @media screen and (max-width: 428px) {
      padding: 20px;
    }
  }

  &__btn {
    @media (max-width: 992px) {
      height: 26px !important;
    }

    &.publish {
      &::v-deep {
        .v-btn__text {
          color: #fff;
        }
      }
    }
  }

  &__cover-image {
    @media (max-width: 992px) {
      position: absolute;
      left: 0;
      top: 67px;
      width: 100%;
      height: 86px;
      display: flex;
      align-items: center;
    }

    .article-create-form__btn {
      @media (max-width: 992px) {
        margin: 0 !important;
      }
    }
  }

  &__link {
    display: inline-block;
    color: #898989;
    font-size: 13px;
    transition: opacity 0.25s;
    display: flex;
    align-items: center;

    @media (max-width: 992px) {
      font-size: 10px;
    }

    &:hover {
      opacity: 0.75;
    }

    img {
      margin-right: 6px;
    }
  }

  &__btn.v-btn {
    border-radius: 4px;
    padding: 0;
    // box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24);

    @media (max-width: 992px) {
      // margin-left: 10px !important;
    }

    &::v-deep {
      .v-btn__content {
        width: 100%;
        height: 100%;
      }

      .v-btn__text {
        display: flex;
        align-items: center;
        height: 100%;
        padding-left: 16px;
        padding-right: 16px;
        cursor: pointer;

        @media (max-width: 992px) {
          font-size: 10px;
          line-height: 12px;
          padding-left: 14px;
          padding-right: 14px;
        }
      }
    }
  }

  &__field {
    position: relative;

    .article-create-form__control {
      padding: 0;

      &.v-input--is-label-active + .article-create-form-field__label {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &__title {
    .article-create-form__control {
      font-size: 30px;
    }
  }

  &__author {
    .article-create-form__control {
      font-size: 12px;
    }

    .article-create-form__title-short:before {
      @media (min-width: 992px) {
        padding-top: 0;
        padding-bottom: 0;
        font-size: 12px;
        line-height: 14px;
        top: 9px;
      }
    }
  }

  &__title-short {
    &:before {
      @media (min-width: 992px) {
        position: absolute;
        content: attr(data-title);
        top: 0;
        right: 100%;
        padding: 6px 12px;
        color: #C4C4C4;
        border-right: 1px solid #E2E2E2;
        margin: 0 30px;
        opacity: 0;
        visibility: hidden;
        transition: opacity .2s;
      }
    }
  }

  &__filename {
    color: #898989;
    font-size: 12px;
  }

  .v-btn__text {
    color: #212121;
  }

  &__editor {
    // position: relative;

    &-btn {
      display: none;

      @media (min-width: 992px) {
        display: block;
        position: absolute;
        top: 0;
        left: -100px;
        margin: 0 30px;
        transition: opacity .25s;

        &:hover,
        &:focus {
          opacity: 0.75;
        }
      }
    }
  }
}

.editor {
  &::v-deep {
    .ql-editor {
      padding: 0;
      font-size: 18px;
      font-family: 'MuseoSansCyrl', sans-serif;

      // &:focus {
      //   outline: green;
      // }

      p {
        font-size: 16px;
        line-height: 180%;
        color: #212121;
      }
    }

    .ql-editor.ql-blank::before {
      left: 0;
      right: 0;
      font-style: normal;
      font-size: 18px;
      color: #898989;
      font-family: 'MuseoSansCyrl', sans-serif;
    }

    .ql-bubble .ql-tooltip {
      background: #212121;
      border-radius: 8px;

      // .ql-image {
      //   display: none;
      // }
    }

    .ql-bubble .ql-tooltip:not(.ql-flip) .ql-tooltip-arrow {
      border-bottom-color: #212121;
    }

    .ql-bubble .ql-stroke {
      stroke: #fff;
    }

    .ql-bubble.ql-toolbar button,
    .ql-bubble .ql-toolbar button {
      transition: opacity 0.15s;
    }

    .ql-bubble.ql-toolbar button:hover,
    .ql-bubble .ql-toolbar button:hover,
    .ql-bubble.ql-toolbar button:focus,
    .ql-bubble .ql-toolbar button:focus,
    .ql-bubble.ql-toolbar button.ql-active,
    .ql-bubble .ql-toolbar button.ql-active,
    .ql-bubble.ql-toolbar .ql-picker-label:hover,
    .ql-bubble .ql-toolbar .ql-picker-label:hover,
    .ql-bubble.ql-toolbar .ql-picker-label.ql-active,
    .ql-bubble .ql-toolbar .ql-picker-label.ql-active,
    .ql-bubble.ql-toolbar .ql-picker-item:hover,
    .ql-bubble .ql-toolbar .ql-picker-item:hover,
    .ql-bubble.ql-toolbar .ql-picker-item.ql-selected,
    .ql-bubble .ql-toolbar .ql-picker-item.ql-selected {
      opacity: 0.3;
    }
  }
}

.article-create-form__control {
  &::v-deep {
    input {
      caret-color: rgba(0, 0, 0, 0.87);
      padding: 0px;
    }

    input::placeholder,
    input::-webkit-input-placeholder,
    input::-moz-placeholder,
    input:-moz-placeholder,
    input:-ms-input-placeholder {
      color: #898989;
    }

    .v-input__control {
      .v-input__slot:before,
      .v-input__slot::after {
        display: none;
      }
    }

    .v-text-field {
      padding-top: 0px;
      margin-top: 0px;
    }
  }
}

.article-create-form-field {
  font-family: MuseoSansCyrl;
  color: #757575;
  &__title {
    margin: 60px 0 15px;
    font-size: 40px;
    font-weight: 600;
    line-height: 100%;
  }
  &__author {
    font-size: 16px;
    font-weight: 400;
    line-height: 100%;
  }
  &__text {
    &::v-deep {
      .quill-editor {
        .ql-toolbar {
          position: sticky;
          padding: 30px 0 20px;
          z-index: 99;
          top: 0px;
          background: #fff;
          border: none !important;
          border-bottom: 1px solid #D9D9D9 !important;
          @media screen and (max-width: 599px) {
            top: 60px;
          }
            .ql-formats {
            margin-right: 0 !important;
            .ql-stroke {
              stroke: #757575;
            }
            .ql-fill {
              fill: #757575;
            }
            .ql-picker {
              color: #757575;
            }
            .ql-header, .ql-picker {
              font-family: MuseoSansCyrl;
            }
            .ql-active {
              background: rgba(0, 0, 0, 0.2);
            }
          }
        }
        .ql-container {
          border: none !important;
          padding-top: 30px;
          .ql-editor p {
            color: #212121;
            font-size: 18px;
            font-weight: 400;
            line-height: 160%;
          }
        }
      }
    }
  }
  &__cover {
    position: relative;
    max-width: 450px;
  }
}

.align-items-center {
  align-items: center !important;
}

</style>

<i18n locale="en" lang="yaml">
title: Title
titleShort: Title
author: Author
authorShort: Author
cover: Article cover
cancel: Cancel
publish: Publish
placeholderEditor: Insert text here
placeholderEditorShort: Text
goBack: Go back
articleCover: Add cover
articleCoverChange: Change cover
preview: Preview
notFound: Failed to get article
removePublication: Unpublish
</i18n>

<i18n locale="ru" lang="yaml">
title: Заголовок статьи
titleShort: Заголовок
author: Автор статьи
authorShort: Автор
cover: Обложка статьи
cancel: Отменить создание
publish: Опубликовать
placeholderEditor: Текст статьи
placeholderEditorShort: Текст
goBack: Назад
articleCover: Добавить обложку
articleCoverChange: Заменить обложку
preview: Предпросмотр
notFound: Не удалось получить статью
removePublication: Снять с публикации
</i18n>
